/* eslint-disable react-hooks/static-components */
import React, {useCallback, useEffect, useState, useRef} from "react";
import {Col, Row} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import ZeusButton from "../../../components/Button";
import ListView from "../../../components/ContentDisplay/ListView";
import SelectCreator from "../../../components/Forms/components/SelectCreator";
import InputCreator from "../../../components/Forms/components/InputCreator";
import SafeView from "../../../components/safeView/SafeView";
import {isProdMode} from "../../../constants/mode";
import {
  requestSensorCalibrationData,
  requestSensorCalibrationHistoryData,
  requestSummarySensorData,
  runSensorCalibration,
  storeSensorCalibrationData,
  storeSensorCalibrationHistoryData,
  storeSummarySensorData,
} from "../../../redux/Actions/controllers.actions";
import {
  getCalibrationLogs,
  getInventorySensorSummary,
  getSensorCalibrationCommands,
} from "../../../redux/Selectors/controllers.selectors";
import "./calibration.scss";

const RESET_SLOPE_ID = 15;
const ANALOG_TITLE = "analog";

const Calibration = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const sensorData = useSelector(getInventorySensorSummary);
  const calibrationCommands = useSelector(getSensorCalibrationCommands);
  const calibrationHistory = useSelector(getCalibrationLogs);
  const intervalRef = useRef();
  const id = location?.state?.id;
  const title = location?.state?.title;
  const [payload, setPayload] = useState({sensorId: id});
  const [helpText, setHelpText] = useState("");
  const clearTimeInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  useEffect(() => {
    const dataCall = (trackThisPromise = false) => {
      dispatch(requestSummarySensorData(id, trackThisPromise));
      dispatch(requestSensorCalibrationHistoryData(id, trackThisPromise));
    };
    clearTimeInterval();
    dispatch(requestSensorCalibrationData(id));
    dataCall(true);
    if (isProdMode) {
      intervalRef.current = setInterval(dataCall, 5000);
    }
    return () => {
      clearTimeInterval();
      dispatch(storeSummarySensorData({}));
      dispatch(storeSensorCalibrationData([]));
      dispatch(storeSensorCalibrationHistoryData({}));
    };
  }, [clearTimeInterval, dispatch, id]);

  const handleInputChange = (event) => {
    const {value, name} = event.target;
    if (name === "id") {
      const selectedCommand = calibrationCommands.find(
        (command) => command.value === value
      );
      if (selectedCommand) {
        setHelpText(selectedCommand.help_text);
      }
      if (value === RESET_SLOPE_ID && title === ANALOG_TITLE) {
        setPayload((payload) => ({...payload, id: value, value: "0"}));
        return;
      }
    }
    setPayload((payload) => ({...payload, [name]: value}));
  };

  const handleSave = () => {
    dispatch(runSensorCalibration(payload));
  };

  const RenderSensorValue = () => {
    return (
      <span className={sensorData.in_alarm ? "text-danger" : "text-success"}>
        {sensorData.value}
      </span>
    );
  };

  return (
    <SafeView>
      <div className="calibration-container">
        <div>
          <h2>
            {sensorData.description} #{sensorData.pin}
          </h2>
          <hr />
          <Row>
            <Col lg={6}>
              <SelectCreator
                label="Calibration Type"
                id="id"
                name="id"
                value={payload.id}
                options={calibrationCommands}
                onChange={handleInputChange}
                isRequired
                isSearchable
                help_text={helpText}
                large
              />
              <InputCreator
                label={"Reference Value"}
                disabled={payload.id === RESET_SLOPE_ID}
                placeholder={"Enter a value"}
                name={"value"}
                value={payload.value}
                onChange={handleInputChange}
                isRequired
                type={"number"}
                step={"any"}
                large
              />
            </Col>
          </Row>
          <Row className={"col-md-6"}>
            <Col md={2}>
              <div className="form-control-label">Result</div>
            </Col>
            <Col md={8}>
              <RenderSensorValue />
            </Col>
            <Col md={1}>
              <ZeusButton onClick={handleSave}>Calibrate</ZeusButton>
            </Col>
          </Row>
        </div>
        <hr />
        <div>
          <h2>History</h2>
          <ListView
            listItems={calibrationHistory}
            listHeaders={["timestamp", "message", "user"]}
          />
        </div>
      </div>
    </SafeView>
  );
};

export default Calibration;
